import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { DollarSign, Percent, CalendarCheck, Info } from "lucide-react";

const PaymentSchedule = [
  {
    id: 1,
    name: "On Booking",
    percentage: 2,
    date: "Apr 2025",
    completion: null,
  },
  {
    id: 2,
    name: "1st Installment",
    percentage: 2,
    date: "Jun 2025",
    completion: null,
  },
  {
    id: 3,
    name: "2nd Installment",
    percentage: 2,
    date: "Jan 2026",
    completion: null,
  },
  {
    id: 4,
    name: "3rd Installment",
    percentage: 2,
    date: "Jun 2026",
    completion: null,
  },
  {
    id: 5,
    name: "4th Installment",
    percentage: 2,
    date: "May 2027",
    completion: "20% Construction Completion*",
  },
  {
    id: 6,
    name: "5th Installment",
    percentage: 9,
    date: "Nov 2027",
    completion: "40% Construction Completion*",
  },
  {
    id: 7,
    name: "6th Installment",
    percentage: 15,
    date: "Apr 2028",
    completion: "60% Construction Completion*",
  },
  {
    id: 8,
    name: "7th Installment",
    percentage: 18,
    date: "Oct 2028",
    completion: "80% Construction Completion*",
  },
  {
    id: 9,
    name: "8th Installment",
    percentage: 20,
    date: "Jun 2029",
    completion: "100% Construction Completion*",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const cardVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
    },
  },
  hover: {
    y: -5,
    scale: 1.02,
    transition: { type: "spring", stiffness: 400, damping: 17 },
  },
};

const Payment = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-[#f8f9fa] to-[#e9ecef]"
      id="payments"
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute h-2 w-2 rounded-full bg-gradient-to-r from-gold-300 to-gold-500"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.3,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50],
              x: [0, Math.random() * 100 - 50],
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Animated gradient orbs */}
      <motion.div
        className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-gradient-to-br from-gold-300/20 to-gold-500/20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
      />
      <motion.div
        className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-gradient-to-tr from-gold-500/20 to-gold-300/20 blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.6, 0.4],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
          delay: 1,
        }}
      />

      <div className="container relative mx-auto px-4 max-w-7xl">
        <div className="text-center mb-16">
          <motion.div
            className="mb-6 inline-flex items-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-gold-400" />
            <span className="mx-4 text-sm font-semibold uppercase tracking-widest text-gold-500">
              Flexible Payment Plan
            </span>
            <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-gold-400" />
          </motion.div>

          <motion.h2
            className="text-4xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Your Path to{" "}
            <span className="bg-gradient-to-r from-gold-400 to-gold-600 bg-clip-text text-transparent">
              Luxury Living
            </span>
          </motion.h2>

          <motion.p
            className="text-lg text-slate-600 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
          >
            Structured payment plan aligned with construction milestones, making
            your dream home more accessible
          </motion.p>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {PaymentSchedule.map((payment) => (
            <motion.div
              key={payment.id}
              variants={cardVariants}
              whileHover="hover"
              className="h-full"
            >
              <div className="h-full bg-white/80 backdrop-blur-sm border border-silver-300 rounded-xl shadow-sm transition-all duration-300 hover:border-gold-300 p-6 group">
                <div className="pb-4 border-b border-silver-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-medium text-midnightBlack group-hover:text-gold-500 transition-colors">
                      {payment.name}
                    </h3>
                    <div className="relative">
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-gold-400 to-gold-600 rounded-full blur-md"
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.5, 0.8, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          repeatType: "reverse",
                        }}
                      />
                      <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 text-white font-bold">
                        {payment.percentage}
                        <Percent className="ml-1 w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CalendarCheck className="w-5 h-5 text-gold-500 mt-0.5" />
                      <div>
                        <p className="text-lg font-semibold text-midnightBlack">
                          {payment.date}
                        </p>
                        {payment.completion && (
                          <p className="text-sm text-slate-500 mt-1">
                            {payment.completion}
                          </p>
                        )}
                      </div>
                    </div>

                    <motion.div
                      className="w-full h-2 bg-silver-200 rounded-full overflow-hidden"
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{
                        duration: 1,
                        delay: 0.5 + payment.id * 0.1,
                      }}
                    >
                      <motion.div
                        className="h-full bg-gradient-to-r from-gold-300 to-gold-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${payment.percentage * 5}%` }}
                        transition={{
                          duration: 1.5,
                          delay: 0.7 + payment.id * 0.1,
                          ease: "easeOut",
                        }}
                      />
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="mt-10 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.5 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-silver-300">
            <Info className="w-4 h-4 text-gold-500" />
            <p className="text-sm text-slate-600">
              Payment milestones are tied to construction progress and subject
              to adjustment
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Payment;
